import React from "react"

import { DEFAULT_LOCALE } from "constants/languages"

import type { PromotionCategory } from "services/promotions/type"

import { slugify } from "./BlocksEditor/helpers/slugify"
import { usePromotionEditor } from "./hooks/usePromotionEditor"
import { PromotionEditorActions } from "./PromotionEditorActions"
import { PromotionGlobalFields } from "./PromotionGlobalFields"
import { PromotionLocalesBar } from "./PromotionLocalesBar"
import { PromotionTranslationFields } from "./PromotionTranslationFields"

type Props = {
  mode: "create" | "edit"
  editor: ReturnType<typeof usePromotionEditor>
  /** Embedded: the host closes the editor, since going back would leave its page. */
  onCancel?: () => void
}

export const PromotionEditorForm = ({ mode, editor, onCancel }: Props) => {
  const {
    saving,
    form,
    setForm,
    tr,
    activeLocale,
    setActiveLocale,
    slugEdited,
    setSlugEdited,
    addedLocales,
    availableToAdd,
    setTranslationField,
    addLocale,
    removeLocale,
    submit
  } = editor

  return (
    <div className="text-white rounded-lg shadow-xl w-full mx-auto p-6">
      <form onSubmit={submit} className="space-y-4">
        <PromotionLocalesBar
          activeLocale={activeLocale}
          addedLocales={addedLocales}
          setActiveLocale={setActiveLocale}
          availableToAdd={availableToAdd}
          onSelectAddLocale={addLocale}
          onRemoveLocale={removeLocale}
        />

        <div className="flex flex-col gap-4">
          <PromotionTranslationFields
            key={activeLocale}
            activeLocale={activeLocale}
            tr={tr}
            onTitleChange={(title) => {
              setTranslationField(activeLocale, { title })

              if (!slugEdited && activeLocale === DEFAULT_LOCALE) {
                setForm((prev) => ({ ...prev, slug: title ? slugify(title) : "" }))
              }
            }}
            onShortTitleChange={(v) => setTranslationField(activeLocale, { short_title: v })}
            onDescriptionChange={(html) => setTranslationField(activeLocale, { description: html })}
            onBannerChange={(file) => setTranslationField(activeLocale, { banner_image: file })}
            onThumbnailChange={(file) => setTranslationField(activeLocale, { thumbnail_image: file })}
            onBlocksChange={(blocks) => setTranslationField(activeLocale, { blocks })}
          />

          <PromotionGlobalFields
            slug={form.slug}
            onSlugChange={(v) => {
              setSlugEdited(true)
              setForm((prev) => ({ ...prev, slug: v }))
            }}
            status={form.status}
            onStatusChange={(v) => setForm((prev) => ({ ...prev, status: v }))}
            categories={form.categories}
            onToggleCategory={(category: PromotionCategory, checked: boolean) => {
              setForm((prev) => {
                if (checked) {
                  if (prev.categories.includes(category)) return prev
                  return { ...prev, categories: [...prev.categories, category] }
                }
                return { ...prev, categories: prev.categories.filter((c) => c !== category) }
              })
            }}
            is_active={form.is_active}
            onIsActiveChange={(v) => setForm((prev) => ({ ...prev, is_active: v }))}
            is_featured={form.is_featured}
            onIsFeaturedChange={(v) => setForm((prev) => ({ ...prev, is_featured: v }))}
            start_at={form.start_at}
            onStartAtChange={(date) => setForm((prev) => ({ ...prev, start_at: date ? date.toISOString() : null }))}
            end_at={form.end_at}
            onEndAtChange={(date) => setForm((prev) => ({ ...prev, end_at: date ? date.toISOString() : null }))}
          />
        </div>

        <PromotionEditorActions mode={mode} saving={saving} onCancel={onCancel ?? (() => window.history.back())} />
      </form>
    </div>
  )
}
